/**
 * "Why did it move?" (DESIGN §4.4): one Drawer that holds what the right panel leaves out. Tabs: what changed (the price and equity-value bridges, one short chain per changed decision, the
 * decisions added up, the value explanation), the rate position, the statements, and the risk officer's note. The causes are the engine's own sentences, the same ones the panel prints.
 */
import { RISK_STEWARD_RULE } from "@gxs/metric-defs";
import { useState } from "react";
import type { DecisionFamily, PreviewResult, PreviewTrace } from "@bsim/domain";
import { Drawer } from "../theme/Drawer";
import { GxChain } from "../theme/GxChain";
import { GxPill } from "../theme/GxPill";
import { GxTabs } from "../theme/GxTabs";
import { formatUsdPerShare } from "../format";
import type { CroWatch } from "../preview-worker";
import { QUARTER_END_LOW_KPIS, WORST_QUARTER_END } from "../api/riskFields";
import { deskLabel } from "./labels";
import { PANEL_LABEL, balancesLine, causeText, changeText, labelOfKpi, plainBecause, plainRiskText, type Callout } from "./planRows";
import { GxTerm } from "../theme/GxTerm";
import { giveBackText, scoreRangeText, worthVsPriceLine, valueAtEndText, deltaText, LOOK_THROUGH_LABEL } from "./previewRows";
import { largest, moved, priceEffect, togetherOf, usdChange } from "./preview/traceText";
import { TraceView } from "./preview/TraceView";
import { AlmView } from "./preview/AlmView";
import { StatementsView } from "./preview/StatementsView";
import { limitReading } from "./pastLimit";
import { formatKpi } from "../format";
import type { PreviewViewProps } from "./slotTypes";
import { Link } from "../shell/history";
import { GxRiskTable } from "./SinceYearEnd";
import { RiskCallout } from "./RiskCallout";
import { RiskTable } from "../theme/RiskTable";
import { GxForecastPill } from "../theme/ForecastPill";
import type { RiskModel } from "./riskRows";
import type { RiskProjection } from "../api/riskFields";
import { CARD_LINES_BASIS, type AnsweredCardLine } from "./answeredCards";

/** The strip names the projection\u2019s own start: the round start, or the month a card stopped the year (a projection that looks ahead less than the round). */
const bridgeTitle = (p: RiskProjection): string => `${p.horizonMonths !== undefined && p.horizonMonths < 12 ? "The month the card stopped you" : "Round start"} \u2192 plan year-end`;

export type WhyTab = "changed" | "rates" | "statements" | "note" | "risk";
const TABS: readonly { readonly id: WhyTab; readonly label: string }[] = [{ id: "changed", label: "What changed" }, { id: "rates", label: "Rates" }, { id: "statements", label: "Statements" }, { id: "risk", label: "Risk dashboard" }, { id: "note", label: "Risk officer's note" }];
/** Wide enough for every column of the rate tables (DESIGN §4.4: 560 px); the statements, with their three money columns, take 640. */
export const WHY_DRAWER_WIDTH = "38rem";
export const STATEMENTS_DRAWER_WIDTH = "40rem";

export interface WhyDrawerProps extends Omit<PreviewViewProps, "go" | "focus"> {
  readonly tab: WhyTab; readonly onTab: (tab: WhyTab) => void; readonly onClose: () => void;
  readonly result: PreviewResult | null; readonly watch: CroWatch | null; readonly callout: Callout | null; readonly rangeNotes: readonly string[];
  readonly onFamily: (family: DecisionFamily) => void; readonly opensIn: (family: DecisionFamily) => "open" | number | null; readonly limitsHref?: string | undefined; readonly solo?: boolean | undefined;
  readonly focus?: string | null | undefined;
  /** The answered cards' dollar effects, one line each. */
  readonly cardLines?: readonly AnsweredCardLine[] | undefined;
  /** The risk dashboard's rows (the panel's own), for the Risk dashboard tab. */
  readonly risk?: RiskModel | null | undefined;
}

const BRIDGE_PARTS = [
  ["earningsUsdPerShare", "Earnings the market pays for"], ["growthUsdPerShare", "Growth the market pays for"], ["capitalUsdPerShare", "Capital held per share"], ["riskPremiumUsdPerShare", "Cost of equity"], ["otherUsdPerShare", "Everything else"],
] as const;
const EVE_PARTS = [["loansRate", "Loans"], ["securitiesRate", "Bonds and other securities"], ["depositsRate", "Deposits"], ["swapsRate", "Swaps"], ["otherRate", "Everything else"]] as const;

function Bridge({ result }: { readonly result: PreviewResult | null }) {
  const price = result?.priceBridge, eve = result?.eveBridge;
  if (price === undefined && eve === undefined) return null;
  const cents = (x: number): number => Math.round(x * 100) / 100;
  // The "everything else" row is what the PRINTED parts leave of the printed headline (as the panel's causes do), so the rows add up exactly.
  const tenth = (x: number): number => Math.round(x * 1000) / 1000;
  const priceRows = price === undefined ? [] : BRIDGE_PARTS.map(([k, label]) => ({ k, label, v: k === "otherUsdPerShare" ? cents(BRIDGE_PARTS.reduce((t, [j]) => t + price[j], 0)) - BRIDGE_PARTS.filter(([j]) => j !== k).reduce((t, [j]) => t + cents(price[j]), 0) : cents(price[k]) })).filter((r) => Math.abs(r.v) >= 0.005);
  const eveRows = eve === undefined ? [] : EVE_PARTS.map(([k, label]) => ({ k, label, v: k === "otherRate" ? tenth(EVE_PARTS.reduce((t, [j]) => t + eve[j], 0)) - EVE_PARTS.filter(([j]) => j !== k).reduce((t, [j]) => t + tenth(eve[j]), 0) : eve[k] })).filter((r) => changeText("eve200", r.v) !== changeText("eve200", 0));
  return (
    <section className="gx-why-sec" data-testid="decide.why.bridge">
      {price === undefined ? null : (
        <div>
          <h3 className="gx-h3">Share price against your standing policy</h3>
          <ul className="gx-why-list gx-num">
            {priceRows.map((r) => <li key={r.k}><span>{r.label}</span><b>{formatUsdPerShare(r.v, { signed: true })}</b></li>)}
          </ul>
        </div>
      )}
      {eve === undefined ? null : (
        <div>
          <h3 className="gx-h3">{PANEL_LABEL["eve200"]}, by where it comes from</h3>
          <ul className="gx-why-list gx-num">
            {eveRows.map((r) => <li key={r.k}><span>{r.label}</span><b>{changeText("eve200", r.v)}</b></li>)}
          </ul>
          {balancesLine(eve) === null ? null : <p className="gx-small gx-faint gx-num" data-testid="decide.why.balances">{balancesLine(eve)}</p>}
        </div>
      )}
    </section>
  );
}

function CardAnswers({ lines }: { readonly lines: readonly AnsweredCardLine[] }) {
  if (lines.length === 0) return null;
  return (
    <section className="gx-why-sec" data-testid="decide.why.cards">
      <h3 className="gx-h3">Your card answers</h3>
      {lines.map((l) => <p key={l.eventInstanceId} className="gx-small gx-muted gx-num" data-testid="decide.why.cards.line">{l.line}</p>)}
      <p className="gx-small gx-faint">{CARD_LINES_BASIS}</p>
    </section>
  );
}

function Chains({ trace }: { readonly trace: PreviewTrace | null }) {
  if (trace === null) return <p className="gx-small gx-faint" data-testid="decide.why.working">Working out what each decision did…</p>;
  const decisions = trace.decisions.filter(moved).sort((a, b) => priceEffect(b) - priceEffect(a));
  if (decisions.length === 0) return <p className="gx-muted" data-testid="decide.why.empty">Nothing is changed yet, so your plan matches your standing policy.</p>;
  const together = togetherOf(trace);
  return (
    <section className="gx-why-sec" data-testid="decide.why.chains">
      {decisions.map((l) => {
        const books = largest(l.bookLines ?? [], (b) => b.deltaUsd, 3);
        const causes = [...new Set(Object.values(l.because ?? {}).map(plainBecause))].slice(0, 3);
        return (
          <article key={l.decisionId} className="gx-why-card" data-testid={`decide.why.card.${l.decisionId}`}>
            <GxChain testId={`decide.why.chain.${l.decisionId}`} steps={[
              { id: "decision", label: "Your decision", children: <><b>{l.label}</b>: {l.fromLabel} {"→"} {l.toLabel}</> },
              { id: "book", label: "Balance sheet, year-end", children: books.length === 0 ? "No balance moves over $50k" : books.map((b) => <span key={b.lineId} className="gx-why-bit">{b.lineId} {usdChange(b.deltaUsd)}.</span>) },
              { id: "does", label: "What it does", children: causes.length === 0 ? "Its effects are small." : causes.map((c) => <div key={c}>{c}</div>) },
            ]} />
          </article>
        );
      })}
      <p className="gx-small gx-faint gx-num" data-testid="decide.why.together">
        All together your changes move the share price {deltaText("price", together.total.price ?? 0)}; the decisions one by one add to {deltaText("price", together.oneByOne.price ?? 0)}; the rest is how they interact.
      </p>
    </section>
  );
}

function ValueExplained({ result }: { readonly result: PreviewResult | null }) {
  const lt = result?.lookThrough;
  if (result === null || result === undefined || lt === undefined) return null;
  // One figure and unit lead, the panel's own: dollars a share with the points in brackets; the split and the range follow in points, said in full.
  const give = giveBackText(lt);
  const lines = [
    `${LOOK_THROUGH_LABEL}: ${formatUsdPerShare(Math.round(lt.valueCreatedUsdPerShare * 100) / 100, { signed: true })} a share (${deltaText("rotce", lt.valueCreatedRate).replace(/ points$/, " percentage points")}) of shareholder value, against your twin, the same bank on its standing policy.`,
    `${give.charAt(0).toUpperCase()}${give.slice(1)}, in percentage points.`, scoreRangeText(lt), worthVsPriceLine(result), valueAtEndText(result, worthVsPriceLine(result) !== null),
  ].filter((t) => t !== null);
  return (
    <section className="gx-why-sec" data-testid="decide.why.value">
      <h3 className="gx-h3">Value against your standing policy</h3>
      {lines.map((t) => <p key={t} className="gx-small gx-muted gx-num">{t}</p>)}
    </section>
  );
}

export function RiskNote({ result = null, watch, callout, rangeNotes, onFamily, opensIn, limitsHref, onClose, solo = false }: Pick<WhyDrawerProps, "watch" | "callout" | "rangeNotes" | "onFamily" | "opensIn" | "limitsHref" | "onClose"> & { readonly solo?: boolean | undefined; readonly result?: PreviewResult | null }) {
  if (watch === null) return <p className="gx-muted" data-testid="decide.why.note.none">The risk officer's reading appears once the plan has been worked out.</p>;
  const notes = watch.notes ?? [];
  // The cause the panel prints for the rate-risk reading leads, so one cause has one name before the note's round-start breakdown.
  const lead = watch.items.some((i) => i.kpi === "eve200") ? causeText(result, null, "eve200") : null;
  const moment = (kpi: string | null): string => (kpi !== null && QUARTER_END_LOW_KPIS.has(kpi) ? WORST_QUARTER_END : "year-end");
  return (
    <div className="gx-why-note" data-testid="decide.why.note">
      {callout === null ? null : <RiskCallout callout={callout} onNote={() => undefined} limitsHref={undefined} />}
      {watch.ownLimit === undefined ? null : (
        <p className="gx-why-find" data-testid="decide.why.note.own">
          Your plan breaks your own {formatKpi(watch.ownLimit.kpi, watch.ownLimit.limit)} limit at {watch.ownLimit.quarterEnds} quarter-end{watch.ownLimit.quarterEnds === 1 ? "" : "s"} this year{watch.ownLimit.path === undefined ? "" : ` on ${watch.ownLimit.path}`}.
          {" "}A warning, not a block: {solo ? "you decide" : "your risk officer can decline to attest"}. {RISK_STEWARD_RULE} Repeated breaches lower the regulator's view of your management; one alone may not.
        </p>
      )}
      {lead === null ? null : <p className="gx-why-find" data-testid="decide.why.note.lead"><b>{PANEL_LABEL["eve200"]}, against your standing policy:</b> {lead}</p>}
      {watch.items.map((item, i) => {
        const read = item.kpi === null ? null : limitReading(item.kpi, item.projected, item.limit, item.projected !== item.limit);
        const opens = item.family === null ? "open" : opensIn(item.family);
        // Why a measure the table has no row for is where it is: held here, so the panel's callout stays two lines a limit.
        const why = callout?.rows.find((r) => r.kpi !== undefined && r.kpi === item.kpi)?.because;
        return (
          <div key={`${item.label}${i}`} className="gx-why-find" data-testid="decide.why.note.item">
            <b>{item.kpi === null ? item.label : <GxTerm term={item.kpi}>{labelOfKpi(item.kpi) ?? item.label}</GxTerm>}, {moment(item.kpi)}: {read?.value ?? item.projected} (limit {read?.limit ?? item.limit}{read?.note == null ? "" : `; ${read.note}`})</b>
            {why === undefined ? null : <span className="gx-small gx-muted" data-testid="decide.watch.because">{why}</span>}
            {item.family === null ? null : opens !== "open"
              ? <GxPill tone="neutral" testId="decide.watch.locked">Set by your standing policy{opens === null ? "" : ` · opens in round ${opens}`}</GxPill>
              : <button type="button" className="gx-linkrow gx-small" data-testid="decide.watch.item" onClick={() => { onClose(); onFamily(item.family as DecisionFamily); }}>Open {deskLabel(undefined, item.family)}</button>}
          </div>
        );
      })}
      {watch.flags.map((f, i) => (
        <p key={i} className="gx-why-find" data-testid="decide.watch.gate" data-severity={f.severity}>
          {plainRiskText(f.text)}
          {f.families.filter((family) => opensIn(family) === "open").map((family) => <button key={family} type="button" className="gx-linkrow gx-small" data-testid="decide.watch.lift" onClick={() => { onClose(); onFamily(family); }}>Lift it in {deskLabel(undefined, family)}</button>)}
        </p>
      ))}
      {(watch.others ?? []).map((t, i) => <p key={`${i}${t}`} className="gx-why-find" data-testid="decide.watch.other">{plainRiskText(t)}</p>)}
      {rangeNotes.map((t) => <p key={t} className="gx-why-find" data-testid="decide.watch.range">{plainRiskText(t)}</p>)}
      {notes.length === 0 ? null : <><h3 className="gx-h3">Notes, for information</h3><ul className="gx-why-bullets">{notes.map((n) => <li key={n}>{plainRiskText(n)}</li>)}</ul></>}
      {limitsHref === undefined ? null : <p><Link href={limitsHref}>Edit limits</Link></p>}
    </div>
  );
}

export function WhyDrawer(props: WhyDrawerProps) {
  const { tab, onTab, onClose, result, trace, state, committed, teamId, later, cardSet, riskProjection } = props;
  const [focus] = useState<string | null>(props.focus ?? null);
  const view: PreviewViewProps = { state, trace, cardSet, committed, teamId, later, riskProjection, focus, go: (id) => onTab(id === "statements" ? "statements" : id === "alm" ? "rates" : "changed") };
  return (
    <Drawer title="Why did it move?" width={tab === "statements" ? STATEMENTS_DRAWER_WIDTH : WHY_DRAWER_WIDTH} onClose={onClose} context={<span className="gx-small gx-faint">Your plan against your standing policy</span>}>
      <div className="gx-why-tabs"><GxTabs tabs={TABS.map((t) => ({ id: t.id, label: t.label, testId: `decide.why.tab.${t.id}` }))} active={tab} onSelect={(id) => onTab(id as WhyTab)} label="Why did it move" /></div>
      <div className="gx-why-body" role="tabpanel">
        {tab === "changed" ? <>
          <Bridge result={result} />
          <Chains trace={trace} />
          <CardAnswers lines={props.cardLines ?? []} />
          <ValueExplained result={result} />
          <details className="gx-why-more"><summary>Every step, decision by decision</summary><TraceView {...view} /></details>
        </> : tab === "rates" ? <>{riskProjection?.bridge === undefined ? null : <GxRiskTable projection={riskProjection} bridge={riskProjection.bridge} title={bridgeTitle(riskProjection)} note={state.status === "computing" ? "updating to your latest change" : "where the plan takes your two rate readings by year-end"} />}<AlmView {...view} /></> : tab === "statements" ? <StatementsView {...view} /> : tab === "risk" ? (props.risk == null ? <p className="gx-muted">The risk dashboard appears once the plan has been worked out.</p> : <div className="gx-card gx-card--forecast"><div className="gx-card-h"><h3 className="gx-h3">Risk dashboard</h3><GxForecastPill basis="worst quarter-end of the year" /></div><RiskTable model={props.risk} narrow onDriver={() => onTab("changed")} /></div>) : <RiskNote {...props} />}
      </div>
    </Drawer>
  );
}
